import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Alert, CircularProgress, IconButton, Chip, Grid, Button
} from '@mui/material';
import { Home, Print, ShoppingCart, ReportProblem, Inventory2 } from '@mui/icons-material';
import axios from 'axios';
import { fmt } from '../utils/currency';

const API  = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const InventoryReorder = () => {
  const navigate = useNavigate();
  const [items,   setItems]   = useState([]);
  const [stock,   setStock]   = useState({});
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/api/payables/items`,      { headers: hdrs() }),
      axios.get(`${API}/api/payables/item-stock`, { headers: hdrs() }),
    ]).then(([i, s]) => { setItems(i.data || []); setStock(s.data || {}); })
      .catch(err => setError(err.response?.data?.error || 'Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  // Items of type Item with a reorder point set, whose on-hand <= reorder point
  const rows = useMemo(() => {
    return items
      .filter(i => i.itemType === 'Item' && i.isActive !== false && (i.reorderPoint || 0) > 0)
      .map(i => {
        const qty = stock[i.itemNo]?.qty || 0;
        const rp  = i.reorderPoint || 0;
        // Suggested = explicit reorderQty, else top up to reorder point
        const suggested = (i.reorderQty || 0) > 0 ? i.reorderQty : Math.max(0, rp - qty);
        return { ...i, qty, rp, suggested, short: rp - qty };
      })
      .filter(r => r.qty <= r.rp)
      .sort((a, b) => a.qty - b.qty);
  }, [items, stock]);

  const critical = rows.filter(r => r.qty <= 0).length;

  if (loading) return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', mt: 4, textAlign: 'center' }}>
      <CircularProgress /><Typography sx={{ mt: 2 }}>Loading…</Typography>
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', mt: 4, mb: 6 }}>
      <style>{`@media print { .no-print { display:none !important; } body { background:#fff !important; } }`}</style>

      <Card elevation={3}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} className="no-print">
            <Box display="flex" alignItems="center">
              <ReportProblem sx={{ mr: 1, color: '#f57c00' }} />
              <Typography variant="h5" sx={{ color: '#e65100' }}>Replenishment — Items to Reorder</Typography>
            </Box>
            <Box display="flex" gap={1}>
              <IconButton onClick={() => window.print()} title="Print"
                sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}>
                <Print />
              </IconButton>
              <IconButton onClick={() => navigate('/supply-chain')} title="Supply Chain Home"
                sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}>
                <Home />
              </IconButton>
            </Box>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Grid container spacing={2} mb={2}>
            <Grid item xs={6} sm={4}>
              <Card elevation={2} sx={{ borderLeft: '4px solid #f57c00' }}>
                <CardContent sx={{ py: 1.5 }}>
                  <Typography variant="h6" fontWeight={800} color="#f57c00" lineHeight={1}>{rows.length}</Typography>
                  <Typography variant="caption" color="text.secondary">Items at / below reorder point</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Card elevation={2} sx={{ borderLeft: '4px solid #c62828' }}>
                <CardContent sx={{ py: 1.5 }}>
                  <Typography variant="h6" fontWeight={800} color="#c62828" lineHeight={1}>{critical}</Typography>
                  <Typography variant="caption" color="text.secondary">Out of stock (critical)</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {rows.length === 0 ? (
            <Alert severity="success" icon={<Inventory2 />}>
              All stocked items are above their reorder points. Nothing to replenish.
            </Alert>
          ) : (
            <>
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Item No</strong></TableCell>
                      <TableCell><strong>Description</strong></TableCell>
                      <TableCell><strong>Home Store</strong></TableCell>
                      <TableCell align="right"><strong>On Hand</strong></TableCell>
                      <TableCell align="right"><strong>Reorder Point</strong></TableCell>
                      <TableCell align="right"><strong>Suggested Order</strong></TableCell>
                      <TableCell align="center"><strong>Status</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map(r => (
                      <TableRow key={r.itemNo} hover sx={{ bgcolor: r.qty <= 0 ? 'error.50' : undefined }}>
                        <TableCell><strong>{r.itemNo}</strong></TableCell>
                        <TableCell>{r.itemName}</TableCell>
                        <TableCell>{r.defaultLocationCode || '—'}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: r.qty <= 0 ? 'error.main' : undefined }}>
                          {fmt(r.qty)} {r.unitOfMeasure}
                        </TableCell>
                        <TableCell align="right">{fmt(r.rp)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: 'primary.main' }}>{fmt(r.suggested)}</TableCell>
                        <TableCell align="center">
                          <Chip size="small" variant="outlined"
                            color={r.qty <= 0 ? 'error' : 'warning'}
                            label={r.qty <= 0 ? 'Out of stock' : 'Low'} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
              <Box mt={2} className="no-print">
                <Button variant="contained" startIcon={<ShoppingCart />}
                  onClick={() => navigate('/payables/orders/new')}
                  sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}>
                  Create Purchase Order
                </Button>
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default InventoryReorder;
